import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { Theme, alpha } from "@mui/material/styles";
import { MouseEvent, memo, useEffect, useRef, useState } from "react";
import { MdClose, MdSearch } from "react-icons/md";
import { useLocation, useSearchParams } from "react-router-dom";

import AppIcon from "@/components/Core/AppIcon";
import { SPORT_PATH_PREFIX } from "@/components/Core/CasinoSportToggle/data";
import PillSwitch from "@/components/Core/PillSwitch";
import { useSiteSearch, useTranslate } from "@/hooks";
import { SEARCH_DEBOUNCE_MS, SEARCH_MAX_LENGTH } from "@/pages/Casino/Games/data/constants";
import { SiteSearchScope } from "@/store/reducers/siteSearch";
import { colors } from "@/theme";

import CasinoResults from "./CasinoResults";
import SportResults from "./SportResults";

const SCOPES: SiteSearchScope[] = ["casino", "sports"];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 999,
    backgroundColor: alpha(colors.prime, 0.05),
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: colors.prime },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.prime },
  },
  "& input::-webkit-search-cancel-button": { display: "none" },
} as const;

const FULL_SCREEN_PAPER = {
  width: "100%",
  height: "100%",
  maxWidth: "none",
  maxHeight: "none",
  margin: 0,
  border: 0,
  borderRadius: 0,
  display: "block",
} as const;

/**
 * The theme's MuiDialog sizes every paper to its content (and floats it with margins on a
 * phone), so a full-screen dialog resized and jumped with every change of results. On the
 * root like the theme's rule, breakpoint included, so these come later and win.
 */
const fullScreenSx = (theme: Theme) => ({
  "& .MuiDialog-paper": {
    ...FULL_SCREEN_PAPER,
    backgroundImage: "none",
    backgroundColor: theme.palette.background.default,
    ...theme.applyStyles("dark", { backgroundColor: theme.palette.background.default }),
    [theme.breakpoints.down(426)]: FULL_SCREEN_PAPER,
  },
});

interface SearchPanelProps {
  initialScope: SiteSearchScope;
  initialTerm: string;
  onClose: () => void;
}

/** Mounted fresh on every open (the dialog unmounts it), so it starts from the props. */
const SearchPanel = ({ initialScope, initialTerm, onClose }: SearchPanelProps) => {
  const t = useTranslate();
  const [scope, setScope] = useState(initialScope);
  const [text, setText] = useState(initialTerm);
  const [query, setQuery] = useState(initialTerm.trim());
  const input = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const timer = setTimeout(
      () => setQuery(text.trim().slice(0, SEARCH_MAX_LENGTH)),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [text]);

  const placeholder = t(scope === "casino" ? "search.placeholder" : "sport.search-placeholder");

  // Every result is a link: following one is picking it. A click that opens a new tab is not.
  const closeOnPick = (event: MouseEvent<HTMLElement>) => {
    const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0;
    if (!modified && (event.target as HTMLElement).closest("a[href]")) onClose();
  };

  return (
    <>
      {/* Stays on top while the results scroll under it. */}
      <Box
        sx={(theme) => ({
          position: "sticky",
          top: 0,
          zIndex: 1,
          borderBottom: "1px solid",
          borderColor: alpha(colors.prime, 0.14),
          backgroundColor: theme.palette.background.default,
          pt: "max(12px, env(safe-area-inset-top))",
          pb: 1.5,
        })}
      >
        <Container maxWidth={false} sx={{ px: { xs: 2, md: 2.5 } }}>
          <Stack gap={1.5}>
            <Stack direction="row" alignItems="center" gap={1}>
              <TextField
                autoFocus
                fullWidth
                size="small"
                type="search"
                inputRef={input}
                value={text}
                onChange={(event) => setText(event.target.value)}
                // Enter closes a phone's keyboard so the results show.
                onKeyDown={(event) => event.key === "Enter" && input.current?.blur()}
                placeholder={placeholder}
                sx={fieldSx}
                slotProps={{
                  htmlInput: {
                    "aria-label": placeholder,
                    maxLength: SEARCH_MAX_LENGTH,
                    enterKeyHint: "search",
                  },
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdSearch size={20} color={colors.prime} aria-hidden />
                      </InputAdornment>
                    ),
                    endAdornment: text ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          edge="end"
                          aria-label={t("game.list.search-clear")}
                          onClick={() => {
                            setText("");
                            input.current?.focus();
                          }}
                        >
                          <MdClose size={18} />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  },
                }}
              />
              <IconButton aria-label={t("common.close")} onClick={onClose} sx={{ flexShrink: 0 }}>
                <MdClose size={22} />
              </IconButton>
            </Stack>

            <PillSwitch
              size="sm"
              ariaLabel={t("search.title")}
              value={SCOPES.indexOf(scope)}
              onChange={(index) => setScope(SCOPES[index])}
              tabs={[
                {
                  key: "casino",
                  label: t("footer.casino"),
                  icon: <AppIcon name="casino" size={14} />,
                },
                {
                  key: "sports",
                  label: t("sport.sports"),
                  icon: <AppIcon name="sports" size={14} />,
                },
              ]}
            />
          </Stack>
        </Container>
      </Box>

      <Container
        maxWidth={false}
        onClickCapture={closeOnPick}
        sx={{ px: { xs: 2, md: 2.5 }, pt: 2, pb: "max(24px, env(safe-area-inset-bottom))" }}
      >
        {scope === "casino" ? <CasinoResults query={query} /> : <SportResults query={query} />}
      </Container>
    </>
  );
};

/**
 * The one search of the site, over whatever page the player is on: games and matches
 * behind one button, opening on the half of the site the player is in.
 */
const SiteSearch = () => {
  const t = useTranslate();
  const { isOpen, scope, term, open, close } = useSiteSearch();
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  const inSports = pathname.startsWith(SPORT_PATH_PREFIX);
  // A sport favourite in the sidebar links here with `?search=<name>`.
  useEffect(() => {
    const fromUrl = params.get("search");
    if (fromUrl === null || !inSports) return;
    open({ scope: "sports", term: fromUrl });
    params.delete("search");
    setParams(params, { replace: true });
  }, [params, inSports]);

  return (
    <Dialog
      fullScreen
      open={isOpen}
      onClose={close}
      aria-label={t("search.title")}
      sx={fullScreenSx}
    >
      <SearchPanel
        initialScope={scope ?? (inSports ? "sports" : "casino")}
        initialTerm={term}
        onClose={close}
      />
    </Dialog>
  );
};

export default memo(SiteSearch);
